import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import useOrganizationContact from '../../hooks/useOrganizationContact';

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
  color: ${({ $onDark }) => ($onDark ? 'rgba(255, 255, 255, 0.95)' : 'inherit')};
`;

const ContactItem = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  font-size: 0.95rem;
  line-height: 1.5;
  color: inherit;
`;

const ContactLink = styled.a`
  color: ${({ $onDark }) => ($onDark ? colors.textPrimary : 'inherit')};
  background: ${({ $onDark }) => ($onDark ? colors.sidebarBg : 'transparent')};
  padding: ${({ $onDark }) => ($onDark ? '0.1rem 0.4rem' : 0)};
  border-radius: 4px;
  text-decoration: ${({ $onDark }) => ($onDark ? 'none' : 'underline')};
  word-break: break-word;

  &:hover {
    opacity: ${({ $onDark }) => ($onDark ? 1 : 0.85)};
    background: ${({ $onDark }) => ($onDark ? colors.primaryDark : 'transparent')};
  }
`;

const BlockTitle = styled.h4`
  margin: 1.25rem 0 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  color: ${({ $onDark }) => ($onDark ? colors.textPrimary : 'inherit')};
  background: ${({ $onDark }) => ($onDark ? colors.sidebarBg : 'transparent')};
  padding: ${({ $onDark }) => ($onDark ? '0.4rem 0.75rem' : 0)};
  border-radius: ${({ $onDark }) => ($onDark ? '6px' : 0)};
  display: ${({ $onDark }) => ($onDark ? 'inline-block' : 'block')};
`;

const ContactRow = ({ icon, label, href, value, onDark }) => (
  <ContactItem>
    <span>{icon}</span>
    <div>
      <strong>{label}</strong>{' '}
      {href ? (
        <ContactLink href={href} $onDark={onDark}>
          {value}
        </ContactLink>
      ) : (
        value
      )}
    </div>
  </ContactItem>
);

/** Téléphone → E-mail → Horaires (ordre harmonisé). */
const PhoneEmailHorairesBlock = ({ block, onDark, t }) => (
  <>
    <ContactRow
      onDark={onDark}
      icon="📱"
      label={t('organizationContact.phoneLabel')}
      href={block.telHref}
      value={block.phone}
    />
    <ContactRow
      onDark={onDark}
      icon="📧"
      label={t('organizationContact.emailLabel')}
      href={block.mailtoHref}
      value={block.email}
    />
    <ContactRow
      onDark={onDark}
      icon="⏰"
      label={t('organizationContact.horairesLabel')}
      value={block.horaires}
    />
  </>
);

/** Bloc contacts entreprise, secrétariat et support (ordre unifié). */
export default function OrganizationContactPanel({
  publicAccess = false,
  showEntreprise = true,
  showSecretariat = true,
  showSupport = true,
  onDark = false
}) {
  const { t } = useTranslation();
  const { organization, loading } = useOrganizationContact({ publicAccess });

  if (loading) {
    return <ContactInfo $onDark={onDark}>{t('common.loading')}</ContactInfo>;
  }

  return (
    <ContactInfo $onDark={onDark}>
      {showEntreprise && (
        <>
          <BlockTitle>{t('organizationContact.entrepriseTitle')}</BlockTitle>
          <ContactRow
            onDark={onDark}
            icon="🏢"
            label={t('organizationContact.nameLabel')}
            value={organization.entreprise.nom}
          />
          <PhoneEmailHorairesBlock block={organization.entreprise} onDark={onDark} t={t} />
        </>
      )}
      {showSecretariat && (
        <>
          <BlockTitle>{t('organizationContact.secretariatTitle')}</BlockTitle>
          <PhoneEmailHorairesBlock block={organization.secretariat} onDark={onDark} t={t} />
        </>
      )}
      {showSupport && (
        <>
          <BlockTitle>{t('organizationContact.supportTitle')}</BlockTitle>
          <PhoneEmailHorairesBlock block={organization.support} onDark={onDark} t={t} />
        </>
      )}
    </ContactInfo>
  );
}
